/**
 * What SHAPE a set may carry on the wire — the ladder's structure and the AMRAP cap rule.
 *
 * Two questions live here, and both are answered from the same two fields (`setType` and
 * `prescribedPercent`), which is why they share a module rather than two:
 *
 * · **Does this set derive its load in session?** A back-off descent, and an AMRAP set carrying a
 *   percent, take their weight from the top set's ACTUAL result during the workout. The backend
 *   therefore refuses an authored weight or an effort target on such a row, and refuses an override
 *   pinning one (`Domain/Progression/SetShapeValidator.cs`, `plan-progression-requirements.md` R10).
 * · **Which AMRAP cap survives a save?** An AMRAP set may carry at most ONE effort cap — RPE or RIR,
 *   never both — and the draft can legitimately hold two while the trainer works. The rule is
 *   resolved at the WIRE rather than by deleting one of the trainer's values the moment a set type
 *   changed.
 *
 * ── Ported from `move-with-me-frontend/.../shared/lib/backoffLadder.ts` (the predicates) and
 *    `.../plans/builder/model/progressionAmrap.ts` (the cap resolution) ────────────────────────
 * PR-B1 took only what a SAVE needs. **PR-B2C completes the port**, because the set table is the
 * first surface that can AUTHOR a set type, and authoring is what can break a block: an ordinary
 * edit — deleting a row, retyping one — can leave a top leading nothing or descents leading
 * nowhere. `backoffBlockHeal` and `backoffPick` arrive here to close that, exactly as PR-B1's
 * header scheduled.
 *
 * Two things stay left upstream, and both read as decisions rather than omissions:
 *
 * 1. **`BlockSet.isOwned` is gone**, with the runtime that projected it. Mobile shares this rule
 *    with its in-gym logger, where a coach-authored row is not the athlete's to relabel. This app
 *    has one runtime — the builder — and it authors every row in a template, so the flag would be a
 *    parameter with one possible value. `ownedHeal` collapses to "always heal".
 * 2. **`WARMUP_SET_TYPE` is NOT redeclared here.** `setCounting.ts` owns the warm-up discriminator
 *    and there is exactly one of it; a second constant beside these four is precisely how two
 *    vocabularies drift. The ladder asks it through `countsAsWork`.
 *
 * A block is ONE top set plus any number of descents, and membership is POSITIONAL — there is no
 * group id — so an exercise holds at most one block. The top is the `Top`-typed set and is never
 * inferred: mobile retired the "first plain working set" guess because it moved silently when an
 * unrelated set was added, and no tie-break makes a guess honest. Marking the top instead is what
 * puts the healing obligation on the surface, which is the bargain this module's second half is.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import type { SetDraft } from './planDraft.types';
import { countsAsWork } from './setCounting';

/**
 * The wire `SetType` values, as the OpenAPI enum MEMBER values rather than their display names —
 * `'AMRAP'`, never `'Amrap'`. `responseToDraft` loads `setType` verbatim, so a loaded set only
 * lines up with a newly-authored one if these match the wire exactly.
 */
export const WORKING_SET_TYPE = 'Working';
/**
 * The ladder's reference set. Behaves exactly like `Working` everywhere else — volume, counting,
 * records, progression — and differs in one way only: it is what the descents take their percentage
 * of, and it is prescribed by value rather than by percentage.
 */
export const TOP_SET_TYPE = 'Top';
export const BACKOFF_SET_TYPE = 'Backoff';
export const AMRAP_SET_TYPE = 'AMRAP';
/**
 * The one wire type this build never AUTHORS — execution ADR §1's conscious deferral. It is named
 * here anyway, because the app still has to recognise one: an inbound dropset from the phone
 * round-trips unchanged, and rendering it as an unknown type rather than as the thing it is would
 * tell the trainer their plan contains something this app cannot read. It stays out of the badge
 * cycle and out of the type menu; `setTypePatch` will still transition a set OUT of it if asked.
 */
export const DROPSET_SET_TYPE = 'Dropset';

/** The two fields the set's structure is expressed in. Any set shape carrying them satisfies these. */
export type ShapedSet = Pick<SetDraft, 'setType' | 'prescribedPercent'>;

export function isTopSet(set: ShapedSet): boolean {
  return set.setType === TOP_SET_TYPE;
}

/** A descent — a working set prescribed as a percentage of the top's actual logged weight (R10). */
export function isBackoffSet(set: ShapedSet): boolean {
  return set.setType === BACKOFF_SET_TYPE;
}

export function isAmrapSet(set: ShapedSet): boolean {
  return set.setType === AMRAP_SET_TYPE;
}

/**
 * Does this set's LOAD resolve in session rather than at authoring time?
 *
 * True for every back-off, and for an AMRAP set that carries a percent — an AMRAP set is a universal
 * set style, so it becomes a ladder member the moment it is given a percent of the top, whatever the
 * exercise's progression type is. This is the exact predicate the backend's validator keys on, and
 * the one the override write half narrows against.
 */
export function derivesLoadInSession(set: ShapedSet): boolean {
  if (isBackoffSet(set)) return true;
  return isAmrapSet(set) && set.prescribedPercent != null;
}

/**
 * The default "% of the top" a fresh descent opens with when nothing prefills it — a 0–1 fraction,
 * matching the wire. A descent must never open blank: the backend requires a percent on every
 * ladder member, so a blank one is a row that cannot be saved the moment it appears.
 */
export const BACKOFF_PERCENT_DEFAULT = 0.8;

/**
 * The index of the exercise's TOP set, or `-1` when it has none.
 *
 * A well-formed exercise holds at most one — `backoffBlockHeal` is what keeps it that way — and a
 * payload that somehow carries two resolves to the FIRST rather than to "ambiguous". A second `Top`
 * behaves like an ordinary working set everywhere else, so refusing to resolve would cost every
 * descent its reference over a row that is otherwise harmless.
 *
 * Returns an index rather than the set so a caller keeps its own richer set type.
 */
export function resolveTopIndex(sets: readonly ShapedSet[]): number {
  return sets.findIndex(isTopSet);
}

/**
 * True when the exercise carries descents but NO top for them to hang off.
 *
 * The heal below makes this unreachable through the UI. It survives as the reading for a shape that
 * arrived that way from the wire — which the set table names inline rather than silently repairing
 * someone else's data mid-render.
 */
export function backoffBlockMissingTop(sets: readonly ShapedSet[]): boolean {
  return sets.some(isBackoffSet) && resolveTopIndex(sets) === -1;
}

/** The single retype that makes a block whole again: which row, and what it becomes. */
export interface BackoffBlockHeal {
  /** Position of the set the heal retypes. */
  readonly index: number;
  /** `Top` (a descent promoted to lead) or `Working` (a row the block no longer has a role for). */
  readonly setType: string;
}

/**
 * The single retype that makes an exercise's block whole again, or `null` when it already is.
 *
 * Every set-level operation applies this, which is what makes "the block always heals" a property
 * of the store rather than a rule each screen has to remember: there is no operation that can leave
 * a top leading nothing or a descent leading nowhere.
 *
 * Total in ONE step, deliberately — the naive "promote the first descent" leaves a lone promoted
 * top with nothing under it, which would need a second pass. The shapes and their answers:
 *
 *   · a top, whatever is under it          → whole, nothing to do. See the divergence below.
 *   · no top, no `Backoff` at all          → no block to repair. A stray percent-bearing AMRAP is
 *                                            left alone: it reads FROM a top without being a member,
 *                                            and it already renders honestly with none resolved.
 *   · no top, one `Backoff` and nothing    → the block is gone, not repairable — promoting the only
 *     else reading from the top              descent would leave a top leading nothing. Working.
 *   · no top, otherwise                    → the first `Backoff` leads, and the rest descend from it.
 *
 * Only a `Backoff` is ever promoted: it is the role that swaps a percentage for a value cleanly,
 * whereas promoting a percent-bearing AMRAP would silently cost it the style it was authored in.
 *
 * ── A DESCENT-LESS TOP SURVIVES HERE, and does not on mobile ─────────────────────────────────
 * Mobile's fifth shape is "top + nothing reading from it → it becomes an ordinary working set", and
 * that is correct THERE because mobile has no way to author a top: `SetTypeSheet` lists `Top` only
 * for the row that already is one, disabled, "shown to be NAMED, not chosen". A top exists on the
 * phone solely because a descent was created against it, so the moment the last descent goes the top
 * has no reason to exist either, and the demotion is unobservable.
 *
 * This app DOES author one — `Top` is in `AUTHORABLE_SET_TYPES`, in the badge cycle and in the menu —
 * and every set-level operation applies this heal, so promoting a working set to `Top` was undone in
 * the same tick by a rule written for a surface that cannot do it. The badge flashed and went back.
 *
 * The backend is the tiebreak, and it permits the shape outright: `SetShapeValidator.ValidateLadder`
 * returns before `RequireSingleTop` when the exercise carries no ladder member at all — its own words
 * are "a composition with no ladder member carries no top requirement". A lone `Top` is a legal,
 * saveable prescription (a heavy single with nothing hanging off it), so demoting it destroys a
 * choice the trainer made and the server would have kept. A top with descents under it is still
 * exactly one top — `changeSetType` demotes the incumbent before promoting, which is where the
 * single-top invariant is enforced.
 *
 * Returned as a DECISION rather than as rewritten sets, so the caller applies it through its own
 * set-type change path — which is also what drops a promoted descent's percentage and gives it back
 * an authorable value.
 */
export function backoffBlockHeal(sets: readonly ShapedSet[]): BackoffBlockHeal | null {
  // Everything still READING from a top, against the block's own MEMBERS. They differ on a
  // percent-bearing AMRAP, which keeps a top meaningful without being a descent to rebuild around.
  const reading = indexesOf(sets, derivesLoadInSession);
  const descents = indexesOf(sets, isBackoffSet);

  // A top is never healed away — see the divergence above. Only descents with nowhere to hang are.
  if (resolveTopIndex(sets) !== -1) return null;

  const firstDescent = descents[0];
  if (firstDescent === undefined) return null;
  // One row reading from a top that does not exist is a block that is GONE, not one that can be
  // rebuilt: promoting the only descent would leave a top leading nothing at all.
  return { index: firstDescent, setType: reading.length === 1 ? WORKING_SET_TYPE : TOP_SET_TYPE };
}

/** What picking "Back-off" means for one exercise — see `backoffPick`. */
export interface BackoffPick {
  /**
   * The exercise has no top yet, so this pick brings the whole block into being: the caller must
   * produce a top as well as the descent. False when a top exists and the pick only adds another.
   */
  readonly startsBlock: boolean;
  /** The percent the new descent opens with — never blank, so the row is savable immediately. */
  readonly percent: number;
}

/**
 * What picking "Back-off" means, given the exercise's sets and the set the pick targets.
 *
 * The percent prefills from the target's own, else the last existing descent's, else the default —
 * so a descending block ("85, 80, 75") is cheap to write, and a set already carrying a fraction
 * keeps it rather than being clobbered.
 */
export function backoffPick(sets: readonly ShapedSet[], target?: ShapedSet | null): BackoffPick {
  const percent =
    target?.prescribedPercent ??
    sets.filter(isBackoffSet).at(-1)?.prescribedPercent ??
    BACKOFF_PERCENT_DEFAULT;
  return { startsBlock: resolveTopIndex(sets) === -1, percent };
}

/**
 * The positions that could serve as this exercise's TOP when a back-off pick has to bring a block
 * into being — the question a surface asks before creating one.
 *
 * An EMPTY result means **don't ask**, and it covers exactly two answerless shapes: a top already
 * exists (so the pick merely adds a descent), or nothing here could be a top at all — every row a
 * warm-up, or every row already deriving its load in session and so unable to be the thing it
 * derives from.
 *
 * The PICKED row is deliberately among the candidates. Excluding it assumed the picked row was
 * certain to become the descent, and which of the two it becomes is exactly what this question
 * decides — on a single-set exercise it is the only sensible answer, and excluding it turned the
 * commonest case into a silent two-new-rows surprise.
 */
export function topSetCandidates(sets: readonly ShapedSet[]): number[] {
  if (resolveTopIndex(sets) !== -1) return [];
  return indexesOf(sets, (set) => countsAsWork(set.setType) && !derivesLoadInSession(set));
}

function indexesOf(
  sets: readonly ShapedSet[],
  predicate: (set: ShapedSet) => boolean,
): number[] {
  const found: number[] = [];
  sets.forEach((set, index) => {
    if (predicate(set)) found.push(index);
  });
  return found;
}

/** The two effort metrics an AMRAP cap can occupy, and the per-set field each one reads. */
const EFFORT_CAP_FIELD = {
  rir: 'prescribedRir',
  rpe: 'prescribedRpe',
} as const satisfies Record<'rir' | 'rpe', keyof Pick<SetDraft, 'prescribedRpe' | 'prescribedRir'>>;

/**
 * RIR is the PREFERRED cap when the exercise measures both. Mobile's historical authoring default,
 * carried over verbatim: the point is not that RIR is better, it is that both clients must drop the
 * SAME one or the two would disagree about a plan neither of them authored.
 */
const CAP_PREFERENCE: readonly (keyof typeof EFFORT_CAP_FIELD)[] = ['rir', 'rpe'];

/**
 * An AMRAP set carrying BOTH caps — the one illegal shape. Legal AMRAP shapes are a bare floor
 * ("5+"), floor + kg, floor + percent (a ladder descent), floor + RPE, and floor + RIR; a capless
 * set runs to failure and is fine.
 */
function hasCapConflict(set: SetDraft): boolean {
  return isAmrapSet(set) && set.prescribedRpe != null && set.prescribedRir != null;
}

/**
 * The effort caps this set actually SHIPS, resolving the at-most-one rule.
 *
 * Which one is kept follows the exercise's own measurement contract (R9: a cap may only sit on a
 * metric the movement measures), falling back to RIR when it measures neither — a set carrying a cap
 * on an unmeasured metric is a 400 either way, and this function's job is the exclusivity rule, not
 * the contract gate.
 *
 * Applied at the wire and nowhere earlier, so a trainer converting a set that already carried both
 * values never watches one of them vanish mid-edit.
 */
export function amrapShippedCaps(
  set: SetDraft,
  measurementFields: readonly MeasurementFieldKey[],
): Pick<SetDraft, 'prescribedRpe' | 'prescribedRir'> {
  if (!hasCapConflict(set)) {
    return { prescribedRpe: set.prescribedRpe, prescribedRir: set.prescribedRir };
  }
  const metric = CAP_PREFERENCE.find((candidate) => measurementFields.includes(candidate)) ?? 'rir';
  const kept = EFFORT_CAP_FIELD[metric];
  return {
    prescribedRpe: kept === 'prescribedRpe' ? set.prescribedRpe : null,
    prescribedRir: kept === 'prescribedRir' ? set.prescribedRir : null,
  };
}
